import React, { useState } from "react";
import { Box, Typography, Collapse, IconButton } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { FieldChangeValue } from "./ChangeHistoryTimeline.types";

interface FieldChangeRowProps {
  fieldName: string;
  oldValue: FieldChangeValue;
  newValue: FieldChangeValue;
}

/**
 * Detects value type and returns a display string.
 * - Primitives: rendered inline as text
 * - null/undefined: displayed as "—"
 * - Arrays: "Array (N items)"
 * - Objects: "Object (N fields)"
 */
const renderValue = (value: FieldChangeValue): string => {
  if (value === null || value === undefined) return "—";
  if (typeof value === "string") return value || "—";
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) return `Array (${value.length} items)`;
  if (typeof value === "object") return `Object (${Object.keys(value).length} fields)`;
  return String(value);
};

/**
 * Determines if a value is complex (object or array) and should have an expand toggle.
 */
const isComplexValue = (value: FieldChangeValue): boolean => {
  if (value === null || value === undefined) return false;
  return typeof value === "object";
};

/**
 * FieldChangeRow renders a single field change showing the field name,
 * old value (red with strikethrough), an arrow separator, and new value (green highlighted).
 * Complex values (objects/arrays) show a summary with an optional expand toggle.
 */
const FieldChangeRow: React.FC<FieldChangeRowProps> = ({
  fieldName,
  oldValue,
  newValue,
}) => {
  const [expanded, setExpanded] = useState(false);

  const hasComplexValue = isComplexValue(oldValue) || isComplexValue(newValue);

  return (
    <Box sx={{ mb: 1, pl: 1 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        {/* Field name label */}
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            minWidth: 120,
            color: "text.secondary",
          }}
        >
          {fieldName}:
        </Typography>

        {/* Old value - red with strikethrough */}
        <Typography
          variant="body2"
          component="span"
          sx={{
            color: "#d32f2f",
            textDecoration: "line-through",
            backgroundColor: "rgba(211, 47, 47, 0.05)",
            px: 0.75,
            py: 0.25,
            borderRadius: 0.5,
            maxWidth: 200,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {renderValue(oldValue)}
        </Typography>

        {/* Arrow separator */}
        <Typography
          variant="body2"
          component="span"
          sx={{ color: "text.disabled", mx: 0.5 }}
        >
          →
        </Typography>

        {/* New value - green highlighted */}
        <Typography
          variant="body2"
          component="span"
          sx={{
            color: "#2e7d32",
            fontWeight: 500,
            backgroundColor: "rgba(46, 125, 50, 0.08)",
            px: 0.75,
            py: 0.25,
            borderRadius: 0.5,
            maxWidth: 200,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {renderValue(newValue)}
        </Typography>

        {/* Expand/collapse toggle for complex values */}
        {hasComplexValue && (
          <IconButton
            size="small"
            onClick={() => setExpanded((prev) => !prev)}
            aria-label={expanded ? "Collapse details" : "Expand details"}
            sx={{ ml: 0.5, p: 0.25 }}
          >
            {expanded ? (
              <ExpandLessIcon fontSize="small" />
            ) : (
              <ExpandMoreIcon fontSize="small" />
            )}
          </IconButton>
        )}
      </Box>

      {/* Expanded details for complex values */}
      {hasComplexValue && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box
            sx={{
              mt: 1,
              ml: "120px",
              display: "flex",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            {isComplexValue(oldValue) && (
              <Box sx={{ flex: 1, minWidth: 180 }}>
                <Typography
                  variant="caption"
                  sx={{ color: "#d32f2f", fontWeight: 600, mb: 0.5, display: "block" }}
                >
                  Old Value:
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    fontSize: "0.75rem",
                    backgroundColor: "rgba(211, 47, 47, 0.04)",
                    border: "1px solid rgba(211, 47, 47, 0.15)",
                    borderRadius: 1,
                    p: 1,
                    m: 0,
                    overflow: "auto",
                    maxHeight: 200,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {JSON.stringify(oldValue, null, 2)}
                </Box>
              </Box>
            )}
            {isComplexValue(newValue) && (
              <Box sx={{ flex: 1, minWidth: 180 }}>
                <Typography
                  variant="caption"
                  sx={{ color: "#2e7d32", fontWeight: 600, mb: 0.5, display: "block" }}
                >
                  New Value:
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    fontSize: "0.75rem",
                    backgroundColor: "rgba(46, 125, 50, 0.04)",
                    border: "1px solid rgba(46, 125, 50, 0.15)",
                    borderRadius: 1,
                    p: 1,
                    m: 0,
                    overflow: "auto",
                    maxHeight: 200,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {JSON.stringify(newValue, null, 2)}
                </Box>
              </Box>
            )}
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

export default FieldChangeRow;
